import { DailyExpertiseStatusCount } from '@/core/api/statistics'
import { useMemo } from 'react'
import BarChartComponent from '@/components/ui/charts/BarChartComponent'
import { useGetEstablishmentExpertiseStatusStatisticsQuery } from '@/core/services/statistics'
import dayjs from 'dayjs'
import { Box, CircularProgress, Typography } from '@mui/material'
import NoResults from '@/assets/svg/no-results.svg?react'
import { t } from '@lingui/macro'

export type DailyExpertisesByStatusComponentProps = {
    establishmentId: string
    title: string
    startDate: Date
    endDate: Date
}

function translateExpertiseStatus(status: string): string {
    switch (status) {
        case 'new':
            return t`New`
        case 'pending':
            return t`Pending`
        case 'in_progress':
            return t`In progress`
        case 'validated':
            return t`Validated`
        case 'refused':
            return t`Refused`
        case 'canceled':
            return t`Canceled`
        case 'completed':
            return t`Completed`
        default:
            return status
    }
}

function DailyExpertisesByStatusComponent(props: DailyExpertisesByStatusComponentProps) {
    const { establishmentId, title, startDate, endDate } = props

    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD')
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD')

    const { data, isLoading, error } = useGetEstablishmentExpertiseStatusStatisticsQuery({
        establishment: establishmentId,
        startDate: formattedStartDate,
        endDate: formattedEndDate
    })

    const transformedData = useMemo(() => {
        if (!data || data.length === 0) return []

        const uniqueStatuses = [...new Set(data.map(item => item.status))]

        return uniqueStatuses.map(status => ({
            label: translateExpertiseStatus(status),
            values: data
                .filter(item => item.status === status)
                .map(item => ({
                    subLabel: item.day,
                    value: item.count
                }))
        }))
    }, [data])

    return (
        <Box>
            {isLoading ? (
                <Box sx={{ minHeight: '300px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : error || !data || data.length === 0 ? (
                <Box sx={{ minHeight: '300px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <NoResults style={{ maxWidth: '40%', height: 'auto' }} />
                </Box>
            ) : (
                <BarChartComponent data={transformedData} title={title} />
            )}
        </Box>
    )
}

export default DailyExpertisesByStatusComponent; 